'use client';

import type { CSSProperties, HTMLAttributes, JSX, ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { createPortal } from 'react-dom';

/**
 * The anchored, modal surface DS2 `Popover` renders — a local stand-in for MUI's `Popover` with the
 * options DS2 used (`anchorOrigin` bottom, `transformOrigin` top, both aligned `left|center|right`;
 * `disableEnforceFocus`; `disableAutoFocus`). Reproduced from MUI rather than swapped for Radix:
 *
 *   - An invisible full-viewport backdrop closes it on click *without* the click reaching the page.
 *   - Escape closes it; focus is neither trapped nor moved in, and is handed back on close.
 *   - Placement is MUI's arithmetic: the panel's top-`alignment` point on the anchor's
 *     bottom-`alignment` point, then shifted to stay 16px inside the viewport, recomputed on resize.
 *   - MUI's Grow transition (opacity + scale from the transform origin, auto duration).
 *
 * Radix's popover positions through a transformed wrapper, which would break the full-screen
 * variant's `position: fixed` panel, and its modal mode traps focus where DS2 opted out.
 */

const MARGIN_THRESHOLD = 16;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
// Above the header and drawers, as MUI's modal layer (1300) was.
const Z_INDEX = 1300;

type Alignment = 'left' | 'center' | 'right';

interface PopoverSurfaceProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    open: boolean;
    onClose: () => void;
    anchorEl: HTMLElement | null;
    alignment: Alignment;
    /** Positioned by its own classes/styles instead of against the anchor. */
    fullScreen?: boolean;
    className?: string;
    style?: CSSProperties;
    children?: ReactNode;
}

const offsetFor = (size: number, alignment: Alignment) =>
    alignment === 'center' ? size / 2 : alignment === 'right' ? size : 0;

/** MUI's `theme.transitions.getAutoHeightDuration`, which Grow uses for `timeout="auto"`. */
function autoDuration(height: number): number {
    if (!height) return 0;
    const constant = height / 36;
    return Math.min(Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10), 3000);
}

interface Placement {
    top?: string;
    left?: string;
    transformOrigin: string;
}

function place(paper: HTMLElement, anchor: HTMLElement | null, alignment: Alignment): Placement {
    const width = paper.offsetWidth;
    const height = paper.offsetHeight;
    let originX = offsetFor(width, alignment);
    let originY = 0;
    const anchorRect = (anchor ?? document.body).getBoundingClientRect();
    let top = anchorRect.top + anchorRect.height - originY;
    let left = anchorRect.left + offsetFor(anchorRect.width, alignment) - originX;

    const heightThreshold = window.innerHeight - MARGIN_THRESHOLD;
    const widthThreshold = window.innerWidth - MARGIN_THRESHOLD;
    if (top < MARGIN_THRESHOLD) {
        const diff = top - MARGIN_THRESHOLD;
        top -= diff;
        originY += diff;
    } else if (top + height > heightThreshold) {
        const diff = top + height - heightThreshold;
        top -= diff;
        originY += diff;
    }
    if (left < MARGIN_THRESHOLD) {
        const diff = left - MARGIN_THRESHOLD;
        left -= diff;
        originX += diff;
    } else if (left + width > widthThreshold) {
        const diff = left + width - widthThreshold;
        left -= diff;
        originX += diff;
    }
    return { top: `${Math.round(top)}px`, left: `${Math.round(left)}px`, transformOrigin: `${originX}px ${originY}px` };
}

type Phase = 'entering' | 'entered' | 'exiting';

export default function PopoverSurface({
    open,
    onClose,
    anchorEl,
    alignment,
    fullScreen = false,
    className,
    style,
    children,
    ...rest
}: PopoverSurfaceProps): JSX.Element | null {
    const [mounted, setMounted] = useState(open);
    const [phase, setPhase] = useState<Phase>('entering');
    const [placement, setPlacement] = useState<Placement | null>(null);
    const [duration, setDuration] = useState(0);
    const paperRef = useRef<HTMLDivElement>(null);
    const restoreFocusTo = useRef<Element | null>(null);

    if (open && !mounted) {
        setMounted(true);
        setPhase('entering');
    }

    const reposition = useCallback(() => {
        const paper = paperRef.current;
        if (paper) setPlacement(fullScreen ? { transformOrigin: '0 0' } : place(paper, anchorEl, alignment));
    }, [anchorEl, alignment, fullScreen]);

    // Open/close transitions, keyed on `open` alone: the anchor ref resolving (which changes
    // `reposition`) must not re-capture the element focus is handed back to.
    const transitionedOpen = useRef(false);
    useLayoutEffect(() => {
        if (!mounted || !paperRef.current || transitionedOpen.current === open) return undefined;
        transitionedOpen.current = open;
        if (open) {
            restoreFocusTo.current = document.activeElement;
            setDuration(autoDuration(paperRef.current.clientHeight));
            const frame = requestAnimationFrame(() => setPhase('entered'));
            return () => cancelAnimationFrame(frame);
        }
        setPhase('exiting');
        const done = window.setTimeout(() => {
            setMounted(false);
            setPlacement(null);
            const target = restoreFocusTo.current;
            if (target instanceof HTMLElement && target.isConnected) target.focus();
        }, duration);
        return () => window.clearTimeout(done);
        // `duration` is the one measured on the way in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mounted]);

    // Place before paint, and again whenever the anchor or alignment changes while open.
    useLayoutEffect(() => {
        if (mounted && open) reposition();
    }, [mounted, open, reposition]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        let resizeTimer: number | undefined;
        const onResize = () => {
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(reposition, 166);
        };
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', onResize);
            window.clearTimeout(resizeTimer);
        };
    }, [open, onClose, reposition]);

    if (!mounted) return null;

    const shown = phase === 'entered';
    const transition =
        phase === 'exiting'
            ? `opacity ${duration}ms ${EASING} 0ms, transform ${Math.round(duration * 0.666)}ms ${EASING} ${Math.round(duration * 0.333)}ms`
            : `opacity ${duration}ms ${EASING} 0ms, transform ${Math.round(duration * 0.666)}ms ${EASING} 0ms`;

    return createPortal(
        <div role="presentation" className="fixed inset-0" style={{ zIndex: Z_INDEX }}>
            <div aria-hidden className="fixed inset-0 bg-transparent" onClick={onClose} />
            <div
                ref={paperRef}
                tabIndex={-1}
                {...rest}
                className={clsx(
                    'absolute min-h-[16px] min-w-[16px] max-h-[calc(100%-32px)] max-w-[calc(100%-32px)] overflow-y-auto overflow-x-hidden outline-none',
                    className,
                )}
                style={{
                    ...style,
                    // After `style`, as MUI wrote its computed position over the paper's own.
                    ...(placement?.top !== undefined ? { top: placement.top, left: placement.left } : null),
                    transformOrigin: placement?.transformOrigin,
                    visibility: placement ? undefined : 'hidden',
                    opacity: shown ? 1 : 0,
                    transform: shown ? 'none' : 'scale(0.75, 0.5625)',
                    transition,
                }}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
}
